import React, { PureComponent } from 'react';
import { st, classes } from './CheckToggle.st.css';
import { dataHooks } from './constants';
import Tooltip from '../Tooltip';
import { InjectedFocusableProps, withFocusable } from '../common/Focusable';
import { ConfirmSmall, Confirm } from '@wix/wix-ui-icons-common';
import { CheckToggleProps } from '.';
import { IconThemeContext } from '../WixDesignSystemIconThemeProvider/IconThemeContext';

/** CheckToggle */
export class CheckToggle extends PureComponent<
  InjectedFocusableProps<CheckToggleProps, HTMLLabelElement>
> {
  static displayName = 'CheckToggle';
  static defaultProps: CheckToggleProps = {
    disabled: false,
    size: 'small',
    skin: 'standard',
  };

  state = {
    checked: !!this.props.checked,
  };

  /**
   * Checks if the component is controlled or uncontrolled.
   * The component is controlled only if prop checked is provided.
   *
   * @returns boolean
   * @private
   */
  _isControlled = () => {
    return this.props.hasOwnProperty('checked');
  };

  /**
   * Toggles checked state and triggers the onChange callback.
   * Except when disabled
   */
  _handleChange = (changeEvent: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = this.state;
    const { onChange } = this.props;

    this.setState({ checked: !checked }, () => {
      if (onChange) onChange(changeEvent);
    });
  };

  /**
   * Renders the toggle itself
   * @returns React.ReactNode
   * @private
   */
  _renderInput = (iconMap: { small: JSX.Element; medium: JSX.Element }) => {
    const { checked } = this._isControlled() ? this.props : this.state;
    const { size, disabled, onChange } = this.props;

    if (!size) return null;

    return (
      <>
        <input
          type="checkbox"
          className={classes.input}
          data-hook={dataHooks.toggle}
          checked={checked}
          disabled={disabled}
          onChange={this._isControlled() ? onChange : this._handleChange}
        />
        <span className={classes.toggle}>{iconMap[size]}</span>
      </>
    );
  };

  /**
   * Renders a tooltip wrapper
   * @returns React.ReactNode
   * @private
   */
  _renderTooltip = (iconMap: { small: JSX.Element; medium: JSX.Element }) => {
    const { tooltipContent, tooltipProps } = this.props;
    return (
      <Tooltip
        dataHook={dataHooks.tooltip}
        content={tooltipContent}
        {...tooltipProps}
      >
        {this._renderInput(iconMap)}
      </Tooltip>
    );
  };

  render() {
    const { checked } = this._isControlled() ? this.props : this.state;
    const {
      dataHook,
      size,
      skin,
      disabled,
      tooltipContent,
      focusableOnFocus,
      focusableOnBlur,
      className,
    } = this.props;

    return (
      <IconThemeContext.Consumer>
        {({ icons = {} }) => {
          const ConfirmIcon = icons.CheckToggle?.Confirm || Confirm;
          const ConfirmSmallIcon =
            icons.CheckToggle?.ConfirmSmall || ConfirmSmall;

          const iconMap = {
            small: <ConfirmSmallIcon />,
            medium: <ConfirmIcon />,
          };

          return (
            <label
              className={st(
                classes.root,
                { checked, size, skin, disabled },
                className,
              )}
              data-hook={dataHook}
              onFocus={focusableOnFocus}
              onBlur={focusableOnBlur}
            >
              {tooltipContent
                ? this._renderTooltip(iconMap)
                : this._renderInput(iconMap)}
            </label>
          );
        }}
      </IconThemeContext.Consumer>
    );
  }
}

export default withFocusable<CheckToggleProps, HTMLLabelElement>(CheckToggle);
